import React, { useEffect, useState } from 'react';
import { Product } from '../types';
import { productService } from '../services/productService';
import ProductCard from '../components/products/ProductCard';

const PAGE_SIZE = 12;
const FETCH_BATCH_SIZE = 100;

const PlanPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceType, setPriceType] = useState('all'); // 'all', 'free', 'paid'
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let allProducts: Product[] = [];
        let offset = 0;
        let total = 0;

        do {
          const res = await productService.getProducts({
            limit: FETCH_BATCH_SIZE,
            offset
          });
          const batch = res.products || [];

          allProducts = [...allProducts, ...batch];
          total = res.total || allProducts.length;
          offset += batch.length;

          if (batch.length === 0) {
            break;
          }
        } while (allProducts.length < total);

        setProducts(allProducts);
        setFilteredProducts(allProducts);
      } catch (err) {
        console.error('ProductsPage Error:', err);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term) ||
        (product.tags && product.tags.some((tag: string) => tag.toLowerCase().includes(term)))
      );
    }

    if (priceType === 'free') {
      filtered = filtered.filter(product => product.price === 0);
    } else if (priceType === 'paid') {
      filtered = filtered.filter(product => product.price > 0);
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [products, searchTerm, priceType]);

  const clearFilters = () => {
    setSearchTerm('');
    setPriceType('all');
  };

  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentPageProducts = filteredProducts.slice(startIndex, startIndex + PAGE_SIZE);
  const visiblePages = Array.from(
    { length: totalPages },
    (_, index) => index + 1
  ).filter((page) => Math.abs(page - currentPage) <= 2 || page === 1 || page === totalPages);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">全部计划</h1>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                搜索计划
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                placeholder="输入计划名称或描述..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                价格类型
              </label>
              <select
                value={priceType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPriceType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">全部</option>
                <option value="free">免费</option>
                <option value="paid">付费</option>
              </select>
            </div>
          </div>

          {(searchTerm || priceType !== 'all') && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                清除筛选条件
              </button>
            </div>
          )}
        </div>

        <div className="mb-6">
          <p className="text-gray-600">
            找到 <span className="font-semibold text-orange-600">{filteredProducts.length}</span> 个计划
            {searchTerm && `，搜索关键词："${searchTerm}"`}
            {priceType === 'free' && '，免费计划'}
            {priceType === 'paid' && '，付费计划'}
          </p>
          {filteredProducts.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              当前第 <span className="font-medium text-gray-700">{currentPage}</span> / <span className="font-medium text-gray-700">{totalPages}</span> 页，
              显示第 <span className="font-medium text-gray-700">{startIndex + 1}</span> -
              <span className="font-medium text-gray-700">{Math.min(startIndex + PAGE_SIZE, filteredProducts.length)}</span> 个计划
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">加载中...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              {searchTerm || priceType !== 'all'
                ? '没有找到符合条件的计划' 
                : '暂无计划'
              }
            </div>
            {(searchTerm || priceType !== 'all') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                清除筛选条件
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {currentPageProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-col items-center gap-4">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 bg-white hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    上一页
                  </button>

                  {visiblePages.map((page, index) => {
                    const previousPage = visiblePages[index - 1];
                    const showEllipsis = previousPage && page - previousPage > 1;

                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[40px] px-3 py-2 rounded-lg border text-sm ${
                            currentPage === page
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-orange-400 hover:text-orange-600'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 bg-white hover:border-orange-400 hover:text-orange-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanPage; 
